import { ReactElement, useEffect, useMemo, useRef } from 'react';

import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { Dayjs } from 'dayjs';

import { DatePickerToolbar, DatePickerToolbarProps, PickerDay, PickerDayProps } from '@mui/x-date-pickers';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

export interface EventDateCalendarProps {
  isMultiDay?: boolean;
  value?: Dayjs[];
  defaultValue?: Dayjs[];
  onChange?: (newDates: Dayjs[] | null) => void;
  onMultiDayChange?: (isMultiDay: boolean) => void;
  orientation?: 'portrait' | 'landscape';
}

function isSameDay(a: Dayjs, b: Dayjs) {
  return a.year() === b.year() && a.month() === b.month() && a.date() === b.date();
}

// Extra props threaded to the slot components through slotProps. They are defined at
// module level (not inside the render body) so React keeps the same component type
// across renders and does not remount every day cell on each change.

interface MultiDayToolbarExtraProps {
  isMultiDay?: boolean;
  onMultiDayChange?: (isMultiDay: boolean) => void;
}

function MultiDayToolbar(props: DatePickerToolbarProps & MultiDayToolbarExtraProps) {
  const { isMultiDay, onMultiDayChange, ...toolbarProps } = props;
  return (
    <Box
      className={toolbarProps.className}
      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', px: 1 }}
    >
      <DatePickerToolbar {...toolbarProps} sx={{ display: 'none' }} />
      <ToggleButtonGroup
        value={isMultiDay ? 'multi' : 'single'}
        exclusive
        onChange={(_event, newValue) => {
          if (newValue !== null && onMultiDayChange) {
            onMultiDayChange(newValue === 'multi');
          }
        }}
        size="small"
      >
        <ToggleButton value="single">Single day</ToggleButton>
        <ToggleButton value="multi">Multi day</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

interface EventDayExtraProps {
  selectedDates?: Dayjs[];
  onDayClick?: (day: Dayjs) => void;
}

function EventDay(props: PickerDayProps & EventDayExtraProps) {
  const { selectedDates, onDayClick, ...dayProps } = props;
  const isSelected = selectedDates?.some((day) => isSameDay(day, dayProps.day)) ?? false;
  return <PickerDay {...dayProps} selected={isSelected} onClick={() => onDayClick?.(dayProps.day)} />;
}

export default function EventDateCalendar(props: EventDateCalendarProps): ReactElement {
  const { isMultiDay, value, onChange, onMultiDayChange } = props;
  const refDates = useRef<Dayjs[]>(value ?? []);

  // Update ref synchronously during render so the day slot sees the new value immediately
  // (useEffect would run AFTER render, causing the calendar to show stale dates)
  refDates.current = value ?? [];

  // The picker re-centers the visible month whenever its value prop changes identity.
  // Callers build fresh Dayjs objects each render, so keep the same object until the
  // first date actually changes; otherwise adding a day in another month snaps the
  // calendar back to the first date's month.
  const firstDateKey = refDates.current[0]?.format('YYYY-MM-DD') ?? null;
  const firstDate = useMemo(
    () => (firstDateKey ? refDates.current[0] : null),
    [firstDateKey]
  );

  useEffect(() => {
    if (!isMultiDay && refDates.current.length > 1) {
      const earliestDate = refDates.current.reduce(
        (acc, current) => (current.valueOf() < acc.valueOf() ? current : acc),
        refDates.current[0]
      );
      refDates.current = [earliestDate];
      onChange?.(refDates.current);
    }
  }, [isMultiDay, onChange]);

  const handleDayClick = (day: Dayjs) => {
    if (isMultiDay) {
      if (refDates.current && refDates.current.some((d) => isSameDay(d, day))) {
        if (refDates.current.length > 1) {
          refDates.current = refDates.current.filter((d) => !isSameDay(d, day));
        }
      } else {
        refDates.current = day ? [...refDates.current, day] : [];
      }
    } else {
      if (refDates.current.length === 1 && isSameDay(refDates.current[0], day)) {
        return;
      }
      refDates.current = [day];
    }

    onChange?.(refDates.current);
  };

  // Typed as the slot's own props plus our extras; assigning a variable (not an object
  // literal) sidesteps excess-property checks without resorting to casts.
  const toolbarSlotProps: Partial<DatePickerToolbarProps> & MultiDayToolbarExtraProps = {
    isMultiDay,
    onMultiDayChange,
  };
  const daySlotProps: Partial<PickerDayProps> & EventDayExtraProps = {
    selectedDates: refDates.current,
    onDayClick: handleDayClick,
  };

  return (
    <Box
      sx={{
        width: '100%',
        '& .MuiPickersLayout-root': {
          minWidth: 'unset',
          backgroundColor: 'transparent',
        },
        '& .MuiDateCalendar-root': {
          width: '100%',
          maxWidth: '100%',
        },
        '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
          justifyContent: 'space-around',
        },
        '& .MuiPickersCalendarHeader-root': {
          paddingLeft: 1,
          paddingRight: 1,
        },
      }}
    >
      <StaticDatePicker
        value={firstDate}
        orientation={props.orientation}
        slots={{
          toolbar: MultiDayToolbar,
          day: EventDay,
        }}
        slotProps={{
          toolbar: toolbarSlotProps,
          day: daySlotProps,
          actionBar: {
            actions: [],
          },
        }}
      />
    </Box>
  );
}
